import classNames from 'classnames';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, DialogContainer, DialogFooter, DialogHeader, Message } from '@wg/wows-react-uikit';

import { PATROLS_RESPONSE_OK_STATUS, rewardsClaim, rewardsRefresh } from '~/api/patrols';
import { REWARD_BUNDLE_TYPES, REWARD_STATUSES } from '~/constants';
import dwhExport, { DWH_EVENTS } from '~/dwhExport';
import { t } from '~/helpers/localization';
import useMount from '~/hooks/useMount';
import { useAppDispatch, useAppSelector } from '~/store';
import {
  patrolsCurrentBundleConfigSelector,
  patrolsCurrentRewardsSelector,
  patrolsHasAllNewRewardsSelector,
  patrolsHasClaimableRewardsSelector,
  patrolsHasNewRewardsSelector,
  patrolsHasPendingRewardsSelector,
  patrolsRewardsSelector,
} from '~/store/selectors/patrolsSelectors';
import { getPatrolsAccountStateThunk } from '~/store/slices/patrolsSlice';
import { ShortPolling } from '~/utils/shortPolling';
import { sendErrorNotification } from '~/web2ClientAPI/base';

import PatrolsReward from '~/pages/Patrols/components/PatrolsReward/PatrolsReward';
import {
  PATROLS_POLLING_INTERVAL,
  PATROLS_POLLING_RESPONSE_INTERVAL,
  PATROLS_REWARDS_ACTIONS,
} from '~/pages/Patrols/constants';

import styles from './PatrolsRewardsDialog.module.scss';
import { CONTENT_STATE, getContent, getInitialContentState } from './helpers';

import type { IDialog } from '~/types/declaration';

type IRewardActionFn = () => ReturnType<typeof rewardsClaim> | ReturnType<typeof rewardsRefresh>;

type IPatrolsRewardsDialog = IDialog<{
  action?: PATROLS_REWARDS_ACTIONS;
}>;

const PatrolsRewardsDialog: React.FC<IPatrolsRewardsDialog> = ({ data, hideDialog }) => {
  const { action = PATROLS_REWARDS_ACTIONS.CLAIM } = data;

  const rewards = useAppSelector(patrolsRewardsSelector);
  const activeBundle = useAppSelector(patrolsCurrentBundleConfigSelector);
  const currentRewards = useAppSelector(patrolsCurrentRewardsSelector) || [];

  const hasClaimableRewards = useAppSelector(patrolsHasClaimableRewardsSelector);
  const hasNewRewards = useAppSelector(patrolsHasNewRewardsSelector);
  const hasAllNewRewards = useAppSelector(patrolsHasAllNewRewardsSelector);
  const hasPendingRewards = useAppSelector(patrolsHasPendingRewardsSelector);

  const dispatch = useAppDispatch();

  const isActionRefresh = action === PATROLS_REWARDS_ACTIONS.REFRESH;
  const isActionClaim = action === PATROLS_REWARDS_ACTIONS.CLAIM;
  const isSuperBundle = activeBundle?.type === REWARD_BUNDLE_TYPES.SUPER;

  const [status, setStatus] = useState({ fetching: false, success: false, error: false, refreshing: false });
  const [contentState, setContentState] = useState<CONTENT_STATE>(
    getInitialContentState({ isActionClaim, hasClaimableRewards, hasNewRewards, hasAllNewRewards: hasAllNewRewards }),
  );

  const content = useMemo(() => {
    return getContent(contentState);
  }, [contentState]);

  const completedRewardsIdxs = useRef(currentRewards.map((reward) => reward.status === REWARD_STATUSES.COMPLETED));
  const activeBundleId = useRef(activeBundle?.id);

  const Poller = useRef<ShortPolling>();

  useMount(() => {
    const updateAccountState = async (signal?: AbortSignal) => {
      await dispatch(getPatrolsAccountStateThunk(signal));
    };

    Poller.current = new ShortPolling(updateAccountState, PATROLS_POLLING_INTERVAL, true);
    void Poller.current.start();

    return () => {
      Poller.current?.stop();
    };
  });

  const handleRewardAction = useCallback(
    async (actionFn: IRewardActionFn) => {
      setStatus((status) => ({ ...status, fetching: true }));

      try {
        const response = await actionFn();
        await dispatch(getPatrolsAccountStateThunk());

        if (response?.status === PATROLS_RESPONSE_OK_STATUS) {
          setStatus((status) => ({ ...status, fetching: false, success: true }));
        } else {
          throw response;
        }
      } catch (error: unknown) {
        setStatus((status) => ({ ...status, fetching: false, error: true }));
        setContentState(CONTENT_STATE.ERROR);
        sendErrorNotification();
      }
    },
    [dispatch],
  );

  const handleClaim = useCallback(async () => {
    dwhExport.push(DWH_EVENTS.PATROLS.MODAL_CLAIM_CLICK);

    await handleRewardAction(rewardsClaim);
    Poller.current?.setInterval(PATROLS_POLLING_RESPONSE_INTERVAL);
  }, [handleRewardAction]);

  const handleRefresh = useCallback(async () => {
    dwhExport.push(DWH_EVENTS.PATROLS.MODAL_REFRESH_CLICK);

    if (hasClaimableRewards) {
      void handleRewardAction(rewardsClaim);
      return;
    }

    await handleRewardAction(rewardsRefresh);
    Poller.current?.setInterval(PATROLS_POLLING_RESPONSE_INTERVAL);
  }, [handleRewardAction, hasClaimableRewards]);

  const buttonAction = isActionRefresh ? handleRefresh : handleClaim;

  // Resolve successfull claimed state: handle refresh or show result
  useEffect(() => {
    if (hasPendingRewards || hasClaimableRewards || !status.success || status.refreshing) {
      return;
    }

    if (isActionRefresh) {
      setStatus((status) => ({ ...status, refreshing: true }));
      void handleRefresh();
    } else {
      setContentState(CONTENT_STATE.CLAIMED);
    }
  }, [handleRefresh, hasClaimableRewards, hasPendingRewards, isActionRefresh, status]);

  // Close dialog on bundle refreshed
  useEffect(() => {
    if (activeBundle?.id !== activeBundleId.current) {
      hideDialog();
    }
  }, [activeBundle?.id, hideDialog]);

  // Stop polling on success claimed and rewards statuses resolved
  useEffect(() => {
    if (status.success && !hasPendingRewards) {
      Poller.current?.stop();
    }
  }, [hasPendingRewards, status.success]);

  if (!rewards || !activeBundle) {
    hideDialog();
    return null;
  }

  return (
    <DialogContainer className={styles.container}>
      <DialogHeader>{content.header}</DialogHeader>
      <div className={styles.body}>
        {!!content.message && <div className={styles.text}>{content.message}</div>}
        {!!content.warning && (
          <Message className={styles.warning} type={'warning'} style={'inline'}>
            <span className={styles.error}>{content.warning}</span>
          </Message>
        )}
        <div
          className={classNames(styles.rewards, {
            [styles.pending]: isActionRefresh && (status.fetching || hasPendingRewards),
          })}
        >
          {currentRewards.map((reward, i) => {
            if (completedRewardsIdxs.current[i] || (isActionClaim && reward.status === REWARD_STATUSES.NEW)) {
              return null;
            }

            return (
              <div className={styles.reward} key={`reward_${reward.id}_${i}`}>
                <PatrolsReward
                  reward={rewards[reward.id]}
                  isSuper={isSuperBundle}
                  isGlowing={
                    !(reward.status === REWARD_STATUSES.FAILED && status.error) &&
                    !(reward.status === REWARD_STATUSES.NEW && isActionRefresh)
                  }
                  isClaimed={reward.status === REWARD_STATUSES.COMPLETED}
                  isLosable={reward.status === REWARD_STATUSES.NEW && isActionRefresh && !hasAllNewRewards}
                  isFailed={reward.status === REWARD_STATUSES.FAILED && status.error}
                  isPending={
                    isActionClaim &&
                    (reward.status === REWARD_STATUSES.PENDING ||
                      (reward.status !== REWARD_STATUSES.COMPLETED && status.fetching))
                  }
                />
              </div>
            );
          })}
        </div>
      </div>
      <DialogFooter classNameContainer={styles.footer}>
        {!!content.button && (
          <Button
            isFlat
            onClick={() => void buttonAction()}
            isDisabled={status.fetching || hasPendingRewards}
            classNameBtn={styles.button}
          >
            {content.button}
          </Button>
        )}
        <Button isFlat onClick={hideDialog}>
          {t('Close')}
        </Button>
      </DialogFooter>
    </DialogContainer>
  );
};

export default memo(PatrolsRewardsDialog);
